import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNode, useEditor } from "@craftjs/core";
import {
    FormControl,
    FormLabel,
    TextField,
    Switch,
    FormControlLabel,
    Box,
    Button,
    Select,
    MenuItem,
    Chip,
    Divider,
    IconButton
} from "@mui/material";
import {
    ShoppingCart as CartIcon,
    Store as StoreIcon,
    FavoriteBorder as WishlistIcon,
    Euro as EuroIcon,
    AttachMoney as DollarIcon,
    ArrowBack as ArrowBackIcon,
    Delete as DeleteIcon
} from "@mui/icons-material";
import ViewSwitcher from '../../../Stack/views/ViewSwitcher';
import { getSelectionStyle } from '../../../Stack/utils/stackUtils';
import DetailResolver from '../../../Stack/core/DetailResolver';
import { DNDEditor } from '../../DNDEditor';
import { QwantaProvider } from 'qwanyx';
import { CartProvider, useCartDispatch, cartActions } from './CartContext';
import { Cart } from './Cart';
import { sync } from '../../../../qwanyx-core/api-client';

export const Shop = ({
    name = "Shop",
    // Data source
    productCatalogId = null, // Specific folder ID (hierarchy-based)
    catalogDomain = null,     // Domain-based catalog selection (e.g. "autodin")
    cartCardId = null,
    // Cart configuration
    orderDomain = "autodinStockOrder", // Domain for saving orders
    cartItemProperties = [
        { field: 'designationcourte', label: 'Product Name', enabled: true },
        { field: 'code', label: 'Code', enabled: true },
        { field: 'deslong', label: 'Description', enabled: true },
        { field: 'famille', label: 'Family', enabled: true },
        { field: 'marque', label: 'Brand', enabled: false },
        { field: 'modele', label: 'Model', enabled: false },
        { field: 'brief', label: 'Brief', enabled: false },
        { field: 'category', label: 'Category', enabled: false }
    ],
    cartShowBorder = true,
    cartBorderColor = "#e0e0e0",
    cartShowItemSeparators = true,
    // Display settings
    width = "600px",
    height = "500px",
    viewMode = "grid", // locked to grid/hybrid for shops
    columnsPerRow = 3,
    // UI visibility
    showShopHeader = false,    // Hidden by default
    showSearchInfo = false,    // Hidden by default
    showLoadingState = true,   // Shown by default
    showFacets = false,        // Hidden by default (clean shops)
    showFacetCounts = false,   // Hidden by default (clean display)
    facetConfig = [            // Configurable facets with labels
        { field: 'category', label: 'Category' },
        { field: 'brand', label: 'Brand' },
        { field: 'price_range', label: 'Price Range' }
    ],
    // Search settings
    enableSearch = true,
    showAdvancedSearch = false,
    searchFields = ['title', 'name', 'description', 'category', 'brand', 'tags'],
    searchMode = 'partial', // 'partial', 'exact', 'fuzzy'
    // Shopping features
    enableCart = true,
    enableWishlist = false,
    showPricing = true,
    currency = "EUR",
    // Shopping buttons configuration
    showShoppingButtons = true,
    shoppingButtonsPosition = "bottom", // "top" or "bottom"
    shoppingButtonsAlignment = "center", // "left", "center", "right"
    useSmallButtons = false,
    showWishlistButton = false,
    showShareButton = true,
    // Cart button configuration
    cartButtonPosition = "header", // "header" or "searchbar"
    showCartButtonText = true, // Show "View Cart" text or just icon
    // Templates
    productTemplate = null,
    categoryTemplate = null,
    productDetailTemplate = null, // Template for detail view
    useCompactRenderer = true,
    useFullTemplateMode = true,
    // Template projection configuration
    templateProjection = [
        { field: 'title', enabled: true },
        { field: 'brief', enabled: true }
    ], // Fields to fetch for template variable replacement
    // Layout
    sideBySide = false,
    listWidth = "400px",
    showCapture = false, // Usually disabled for shops
    // Styling
    imageWidth = "120px",
    ratio = "1/1",
    imageClassName = "",
    textClassName = "",
    animateClass = "animate__fadeIn",
    itemGap = 0, // Gap between items in flat display (pixels)
    itemHeight = 120, // Height of each product item in pixels
    showSeparator = false, // Show separator line between items
    separatorWidth = 1, // Separator line width in pixels
    separatorColor = "#e0e0e0", // Separator line color
    style = {},
    // Debug
    debug = false,
    // Delete button visibility
    showDeleteButton = false,
    ...props
}) => {
    const {
        connectors: { connect, drag },
        isSelected,
        isHovered,
    } = useNode((state) => ({
        isSelected: state.events.selected,
        isHovered: state.events.hovered,
    }));

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled
    }));

    // State for product detail view (like StackContainer)
    const [selectedItemForDetail, setSelectedItemForDetail] = useState(null);
    
    // Create a stable ID for this Shop instance to preserve ViewSwitcher state
    // Use a combination of config values to create a stable but unique identifier
    const shopInstanceId = useMemo(() => {
        const configHash = `${productCatalogId || 'none'}-${catalogDomain || 'none'}-${viewMode}`;
        return `shop-${configHash}`;
    }, [productCatalogId, catalogDomain, viewMode]);
    
    
    // Memoize the ViewSwitcher key to prevent unnecessary re-renders
    const viewSwitcherKey = useMemo(() => {
        return `${productCatalogId || 'none'}-${catalogDomain || 'none'}-${viewMode}`;
    }, [productCatalogId, catalogDomain, viewMode]);
    
    // Add effect to handle component mounting/unmounting gracefully
    useEffect(() => {
        if (debug) {
            console.log('[Shop] Component mounted/updated with config:', {
                productCatalogId,
                catalogDomain,
                viewMode,
                shopInstanceId,
                enabled
            });
        }
        
        // Clear selected item when configuration changes to prevent stale state
        setSelectedItemForDetail(null);
    }, [productCatalogId, catalogDomain, viewMode, shopInstanceId, enabled, debug]);

    // Handle item selection for detail view - use useCallback to prevent re-renders
    const handleItemSelect = useCallback((itemId, isShiftKey = false, isCtrlKey = false) => {
        if (enabled) return; // Don't show detail in edit mode
        
        // Set the selected item for detail view
        setSelectedItemForDetail({ _id: itemId });
        
        if (debug) {
            console.log('[Shop] Item selected for detail view:', itemId);
        }
    }, [enabled, debug]);

    // Handle back to list - use useCallback to prevent re-renders
    const handleBackToList = useCallback(() => {
        setSelectedItemForDetail(null);
    }, []);
    

    const containerStyle = {
        width,
        height: height === "100%" ? "100%" : height, // Handle 100% height properly
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
        // Add flex properties for when inside flex containers like TabControl
        ...(height === "100%" && { flex: 1, minHeight: 0 }),
        ...style,
        ...getSelectionStyle(isSelected, isHovered, enabled)
    };

    // Prepare cart info for templates (if cart is connected) - memoize to prevent re-renders
    const cartContext = useMemo(() => {
        return enableCart ? {
            cartEnabled: true,
            cartCardId,
            currency
        } : null;
    }, [enableCart, cartCardId, currency]);
    
    console.log('Shop cartContext:', cartContext);

    return (
        <CartProvider orderDomain={orderDomain}>
            <div
                ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
                name={name}
                style={containerStyle}
                {...props}
            >
            {/* Shop Header */}
            {showShopHeader && (
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    p: 1, 
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: '#f5f5f5'
                }}>
                    <StoreIcon color="primary" />
                    <span style={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {name || 'Shop'}
                    </span>
                    {showPricing && (
                        <Chip 
                            icon={currency === 'EUR' ? <EuroIcon /> : <DollarIcon />} 
                            label={currency} 
                            size="small" 
                            variant="outlined"
                        />
                    )}
                    {enableCart && (
                        <Chip 
                            icon={<CartIcon />} 
                            label="Cart Enabled" 
                            size="small" 
                            color="primary"
                            variant="outlined"
                        />
                    )}
                    {enableCart && !sideBySide && cartButtonPosition === "header" && (
                        <Button
                            size="small"
                            startIcon={showCartButtonText ? <CartIcon /> : undefined}
                            onClick={() => {
                                console.log('View Cart button clicked');
                                setSelectedItemForDetail({ isCartView: true });
                            }}
                            variant="outlined"
                            color="primary"
                            sx={{ 
                                ml: 1,
                                minWidth: showCartButtonText ? undefined : '40px',
                                padding: showCartButtonText ? undefined : '6px 8px'
                            }}
                        >
                            {showCartButtonText ? 'Voir le panier' : <CartIcon />}
                        </Button>
                    )}
                    {enableWishlist && (
                        <Chip 
                            icon={<WishlistIcon />} 
                            label="Favoris" 
                            size="small" 
                            color="secondary"
                            variant="outlined"
                        />
                    )}
                </Box>
            )}

            {/* Main Shop Content - Using Universal Display Engine */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {!productCatalogId && !catalogDomain ? (
                    // Empty state when no catalog is selected
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#666',
                        textAlign: 'center',
                        p: 4
                    }}>
                        <StoreIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                        <h3 style={{ margin: '0 0 8px 0' }}>Aucun catalogue sélectionné</h3>
                        <p style={{ margin: '0 0 16px 0' }}>
                            Veuillez configurer un catalogue de produits pour afficher les articles.
                        </p>
                        <p style={{ fontSize: '12px', color: '#999' }}>
                            Définissez soit "Domaine du catalogue" (ex: "autodin") ou "ID du catalogue de produits" dans le panneau de paramètres.
                        </p>
                    </Box>
                ) : selectedItemForDetail ? (
                    // Product detail view (like StackContainer)
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* Back button header */}
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            p: 1, 
                            borderBottom: '1px solid #eee',
                            backgroundColor: '#f8f9fa'
                        }}>
                            <IconButton 
                                onClick={handleBackToList}
                                size="small"
                                sx={{ mr: 1 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                                {selectedItemForDetail?.isCartView ? 'Panier' : 'Détails du produit'}
                            </span>
                        </Box>
                        
                        {/* Product detail content using Stack DetailResolver */}
                        <Box sx={{ flex: 1, overflow: 'auto' }}>
                            {selectedItemForDetail?.isCartView ? (
                                // Show cart view - Load your configured Cart component
                                <div style={{ padding: '16px' }}>
                                    <Cart 
                                        orderDomain={orderDomain}
                                        title="Panier"
                                        showTitle={true}
                                        showItemCount={true}
                                        showCustomerForm={true}
                                        showOrderButton={true}
                                        itemProperties={cartItemProperties}
                                        showBorder={cartShowBorder}
                                        borderColor={cartBorderColor}
                                        showItemSeparators={cartShowItemSeparators}
                                        width="100%"
                                        maxHeight="none"
                                        customerFormTitle="Your Information"
                                        onOrderSuccess={() => setSelectedItemForDetail(null)}
                                        debug={debug}
                                    />
                                </div>
                            ) : productDetailTemplate && selectedItemForDetail?._id ? (
                                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    {/* Shopping Actions - Top Position */}
                                    {showShoppingButtons && shoppingButtonsPosition === "top" && (
                                        <ShoppingActions 
                                            itemId={selectedItemForDetail._id}
                                            enableCart={enableCart}
                                            showWishlistButton={showWishlistButton}
                                            showShareButton={showShareButton}
                                            alignment={shoppingButtonsAlignment}
                                            useSmallButtons={useSmallButtons}
                                            currency={currency}
                                        />
                                    )}
                                    
                                    {/* Template Card Display */}
                                    <div style={{ flex: 1, overflow: 'auto' }}>
                                        <QwantaProvider _id={selectedItemForDetail._id}>
                                            <DNDEditor
                                                vi={productDetailTemplate} // Template Card ID
                                                enabled={false}
                                                readOnly={true}
                                                stackMode={false}
                                                compact={useCompactRenderer}
                                            />
                                        </QwantaProvider>
                                    </div>
                                    
                                    {/* Shopping Actions - Bottom Position */}
                                    {showShoppingButtons && shoppingButtonsPosition === "bottom" && (
                                        <ShoppingActions 
                                            itemId={selectedItemForDetail._id}
                                            enableCart={enableCart}
                                            showWishlistButton={showWishlistButton}
                                            showShareButton={showShareButton}
                                            alignment={shoppingButtonsAlignment}
                                            useSmallButtons={useSmallButtons}
                                            currency={currency}
                                        />
                                    )}
                                </div>
                            ) : (
                                // Fall back to DetailResolver for template-based or fallback display
                                <DetailResolver
                                    item={selectedItemForDetail}
                                    DetailComponent={(itemProps) => (
                                        <ProductDetailFallback
                                            {...itemProps}
                                            enableCart={enableCart}
                                            showWishlistButton={showWishlistButton}
                                            showShareButton={showShareButton}
                                        />
                                    )}
                                    defaultDetailTemplate={null}
                                    forceDefaultDetail={false}
                                    useCompact={useCompactRenderer}
                                    debug={debug}
                                />
                            )}
                        </Box>
                    </Box>
                ) : (
                    // Main shop display - side-by-side with cart if configured
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: sideBySide && cartCardId ? 'row' : 'column',
                        height: '100%',
                        overflow: 'hidden'
                    }}>
                        {/* Product List */}
                        <Box sx={{ 
                            flex: 1, 
                            overflow: 'hidden',
                            width: sideBySide && cartCardId ? listWidth : '100%'
                        }}>
                            <ViewSwitcher
                                key={viewSwitcherKey}
                                rootId={productCatalogId}
                                di={catalogDomain}
                                defaultView={viewMode}
                                onSelect={handleItemSelect}
                                // Shop-specific UI configuration
                                showSearch={enableSearch}
                                showViewSelector={false} // Shops don't need view switching
                                showFuzzyToggle={showAdvancedSearch}
                                showSearchInfo={showSearchInfo}
                                showLoadingState={showLoadingState}
                                showFacets={showFacets}
                                showFacetCounts={showFacetCounts}
                                searchPlaceholder="Rechercher des produits..."
                                // Shop-specific search fields
                                searchFields={searchFields}
                                facetFields={facetConfig.map(f => f.field)}
                                facetConfig={facetConfig}
                                // Templates
                                itemTemplate={productTemplate}
                                categoryTemplate={categoryTemplate}
                                useCompact={useCompactRenderer}
                                templateProjection={templateProjection}
                                // Styling
                                itemGap={itemGap}
                                itemHeight={itemHeight}
                                showSeparator={showSeparator}
                                separatorWidth={separatorWidth}
                                separatorColor={separatorColor}
                                // Grid view props
                                columnsPerRow={columnsPerRow}
                                imageWidth={imageWidth}
                                ratio={ratio}
                                imageClassName={imageClassName}
                                textClassName={textClassName}
                                animateClass={animateClass}
                                // Cart context passed through to templates
                                extraContext={cartContext}
                                // Delete button visibility
                                showDeleteButton={showDeleteButton}
                                // Additional buttons
                                additionalButtons={enableCart && !sideBySide && cartButtonPosition === "searchbar" && (
                                    <Button
                                        size="small"
                                        startIcon={showCartButtonText ? <CartIcon /> : undefined}
                                        onClick={() => {
                                            console.log('View Cart button clicked');
                                            setSelectedItemForDetail({ isCartView: true });
                                        }}
                                        variant="outlined"
                                        color="primary"
                                        sx={{ 
                                            minWidth: showCartButtonText ? undefined : '40px',
                                            padding: showCartButtonText ? undefined : '6px 8px'
                                        }}
                                    >
                                        {showCartButtonText ? 'Voir le panier' : <CartIcon />}
                                    </Button>
                                )}
                                // Debug
                                debug={debug}
                            />
                        </Box>
                        
                        {/* Cart Display - Only show when sideBySide is true and cart is enabled */}
                        {sideBySide && enableCart && (
                            <Box sx={{ 
                                width: `calc(100% - ${listWidth})`,
                                borderLeft: '1px solid #e0e0e0',
                                padding: '16px'
                            }}>
                                <Cart 
                                    orderDomain={orderDomain}
                                    title="Shopping Cart"
                                    showTitle={true}
                                    showItemCount={true}
                                    showCustomerForm={true}
                                    showOrderButton={true}
                                    itemProperties={cartItemProperties}
                                    showBorder={cartShowBorder}
                                    borderColor={cartBorderColor}
                                    showItemSeparators={cartShowItemSeparators}
                                    width="100%"
                                    maxHeight="none"
                                    customerFormTitle="Your Information"
                                    onOrderSuccess={() => {
                                        // For side-by-side mode, cart stays visible but cleared
                                        // User can continue shopping immediately
                                        console.log('Order completed in side-by-side mode - cart cleared, user can continue shopping');
                                    }}
                                    debug={debug}
                                />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
            </div>
        </CartProvider>
    );
};

// Shopping Actions Component - Configurable shopping buttons
const ShoppingActions = ({ itemId, enableCart, showWishlistButton, showShareButton, alignment = "center", useSmallButtons = false, currency }) => {
    console.log('ShoppingActions component rendered with props:', { itemId, enableCart, showWishlistButton, showShareButton });
    const [item, setItem] = useState(null);
    const dispatch = useCartDispatch();

    useEffect(() => {
        // Fetch item data for shopping actions
        const fetchItemData = async () => {
            console.log('ShoppingActions fetchItemData called with itemId:', itemId);
            try {
                const result = await sync(itemId);
                console.log('ShoppingActions sync result:', result);
                if (result) {
                    console.log('ShoppingActions setting item:', result);
                    setItem(result);
                } else {
                    console.log('ShoppingActions sync returned no data');
                }
            } catch (error) {
                console.error('Error loading item for shopping:', error);
            }
        };

        if (itemId) {
            console.log('ShoppingActions useEffect - calling fetchItemData');
            fetchItemData();
        } else {
            console.log('ShoppingActions useEffect - no itemId provided');
        }
    }, [itemId]);

    const handleAddToCart = () => {
        console.log('ShoppingActions handleAddToCart called', { item, dispatch });
        if (!item) {
            console.log('No item found');
            return;
        }
        
        if (!dispatch) {
            console.log('No dispatch function found');
            return;
        }
        
        console.log('Adding item to cart:', item);
        dispatch(cartActions.addItem(item._id, item, 1));
        
        // Optional: Show a brief success message
        console.log(`Added "${item?.title || item?.name || 'Unknown Product'}" to cart!`);
    };

    const handleAddToWishlist = () => {
        // TODO: Implement wishlist functionality when needed
        alert(`"${item?.title || item?.name || 'Produit inconnu'}" ajouté aux favoris!`);
    };

    const handleShareProduct = () => {
        const url = window.location.href;
        if (navigator.share) {
            navigator.share({
                title: item?.title || item?.name || 'Produit',
                text: item?.brief || 'Découvrez ce produit!',
                url: url
            });
        } else {
            navigator.clipboard.writeText(url);
            alert('Product link copied to clipboard!');
        }
    };

    // Get alignment justification
    const getJustifyContent = () => {
        switch (alignment) {
            case 'left': return 'flex-start';
            case 'right': return 'flex-end';
            case 'center':
            default: return 'center';
        }
    };

    // Get button size and styling
    const buttonSize = useSmallButtons ? "small" : "medium"; // Material-UI uses "small", "medium", "large"
    const buttonPadding = useSmallButtons ? { px: 1.5, py: 0.5 } : { px: 4, py: 1.5 };
    const fontSize = useSmallButtons ? '11px' : '16px';

    return (
        <Box sx={{ 
            display: 'flex', 
            gap: useSmallButtons ? 1 : 2, 
            p: 2,
            flexWrap: 'wrap',
            justifyContent: getJustifyContent(),
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #e0e0e0'
        }}>
            {enableCart && (
                <Button
                    variant="contained"
                    color="primary"
                    size={buttonSize}
                    onClick={handleAddToCart}
                    startIcon={<CartIcon />}
                    sx={{ 
                        ...buttonPadding,
                        fontSize: fontSize,
                        fontWeight: 'bold',
                        minWidth: useSmallButtons ? '60px' : undefined, // Smaller minimum width for small buttons
                        height: useSmallButtons ? '28px' : undefined // Explicit height for small buttons
                    }}
                >
                    Ajouter au panier
                </Button>
            )}
            
            {showWishlistButton && (
                <Button
                    variant="outlined"
                    color="secondary"
                    size={buttonSize}
                    onClick={handleAddToWishlist}
                    startIcon={<WishlistIcon />}
                    sx={{
                        ...buttonPadding,
                        fontSize: fontSize,
                        minWidth: useSmallButtons ? '60px' : undefined,
                        height: useSmallButtons ? '28px' : undefined
                    }}
                >
                    Ajouter aux favoris
                </Button>
            )}
            
            {showShareButton && (
                <Button
                    variant="outlined"
                    color="primary"
                    size={buttonSize}
                    onClick={handleShareProduct}
                    sx={{
                        ...buttonPadding,
                        fontSize: fontSize,
                        minWidth: useSmallButtons ? '60px' : undefined,
                        height: useSmallButtons ? '28px' : undefined
                    }}
                >
                    Partager le produit
                </Button>
            )}
        </Box>
    );
};

// Product detail fallback component - shows all properties when no template is available
// This is used as the DetailComponent prop for DetailResolver
const ProductDetailFallback = ({ item, enableCart = true, showWishlistButton = false, showShareButton = true }) => {
    // Shopping cart actions - must be at the top level
    const dispatch = useCartDispatch();
    
    if (!item) {
        return (
            <Box sx={{ textAlign: 'center', p: 4, color: '#666' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                <div>Aucune donnée de produit disponible</div>
            </Box>
        );
    }
    
    const handleAddToCart = () => {
        console.log('ProductDetailFallback handleAddToCart called', { item, dispatch });
        if (!item) {
            console.log('No item found in ProductDetailFallback');
            return;
        }
        
        if (!dispatch) {
            console.log('No dispatch function found in ProductDetailFallback');
            return;
        }
        
        console.log('Adding item to cart from ProductDetailFallback:', item);
        dispatch(cartActions.addItem(item._id, item, 1));
        
        // Optional: Show a brief success message
        console.log(`Added "${item?.title || item?.name || 'Unknown Product'}" to cart!`);
    };

    const handleAddToWishlist = () => {
        // TODO: Implement wishlist functionality when needed
        alert(`"${item?.title || item?.name || 'Produit inconnu'}" ajouté aux favoris!`);
    };

    const handleShareProduct = () => {
        const url = window.location.href;
        if (navigator.share) {
            navigator.share({
                title: item?.title || item?.name || 'Produit',
                text: item?.brief || 'Découvrez ce produit!',
                url: url
            });
        } else {
            navigator.clipboard.writeText(url);
            alert('Product link copied to clipboard!');
        }
    };

    // Get all non-system properties to display
    const getDisplayProperties = () => {
        const systemFields = ['_id', 'vi', 'card', 'pa', 'ed', 'di', 'qwantaType', 'backup'];
        const properties = [];
        
        Object.keys(item).forEach(key => {
            if (!systemFields.includes(key) && item[key] !== null && item[key] !== undefined) {
                properties.push({ key, value: item[key] });
            }
        });
        
        return properties;
    };

    const displayProperties = getDisplayProperties();

    return (
        <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
            {/* Product Header */}
            <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid #eee' }}>
                <h1 style={{ 
                    margin: '0 0 8px 0', 
                    fontSize: '24px', 
                    color: '#333',
                    fontWeight: 'bold'
                }}>
                    {item.title || item.name || item.firstname ? 
                        `${item.firstname || ''} ${item.name || ''}`.trim() || item.title : 
                        'Untitled Product'
                    }
                </h1>
                
                {item.brief && (
                    <p style={{ 
                        margin: '0 0 16px 0', 
                        fontSize: '16px', 
                        color: '#666', 
                        lineHeight: 1.6
                    }}>
                        {item.brief}
                    </p>
                )}
            </Box>

            {/* Key Product Details */}
            <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 2,
                mb: 3
            }}>
                {/* Special handling for common e-commerce fields */}
                {item.price && (
                    <Box sx={{ p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #4caf50' }}>
                        <strong style={{ color: '#2e7d32' }}>Price:</strong><br />
                        <span style={{ color: '#2e7d32', fontSize: '18px', fontWeight: 'bold' }}>
                            {item.price}
                        </span>
                    </Box>
                )}
                {item.category && (
                    <Box sx={{ p: 2, backgroundColor: '#f3e5f5', borderRadius: 1, border: '1px solid #9c27b0' }}>
                        <strong style={{ color: '#7b1fa2' }}>Category:</strong><br />
                        <span style={{ color: '#666' }}>{item.category}</span>
                    </Box>
                )}
                {item.brand && (
                    <Box sx={{ p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                        <strong style={{ color: '#1976d2' }}>Brand:</strong><br />
                        <span style={{ color: '#666' }}>{item.brand}</span>
                    </Box>
                )}
                {item.stock !== undefined && (
                    <Box sx={{ p: 2, backgroundColor: item.stock > 0 ? '#e8f5e8' : '#ffebee', borderRadius: 1, border: item.stock > 0 ? '1px solid #4caf50' : '1px solid #f44336' }}>
                        <strong style={{ color: item.stock > 0 ? '#2e7d32' : '#d32f2f' }}>Stock:</strong><br />
                        <span style={{ color: '#666' }}>{item.stock}</span>
                    </Box>
                )}
            </Box>

            {/* All Properties Display */}
            <Box sx={{ mb: 3 }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#333' }}>All Properties</h3>
                <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: 1
                }}>
                    {displayProperties.map(({ key, value }) => (
                        <Box key={key} sx={{ 
                            p: 1.5, 
                            backgroundColor: '#f8f9fa', 
                            borderRadius: 1,
                            border: '1px solid #e0e0e0'
                        }}>
                            <div style={{ 
                                fontSize: '12px', 
                                fontWeight: 'bold', 
                                color: '#666',
                                marginBottom: '4px',
                                textTransform: 'capitalize'
                            }}>
                                {key.replace(/([A-Z])/g, ' $1').trim()}:
                            </div>
                            <div style={{ 
                                fontSize: '14px', 
                                color: '#333',
                                wordBreak: 'break-word'
                            }}>
                                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                            </div>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Shopping Actions */}
            <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                mb: 3,
                flexWrap: 'wrap',
                justifyContent: 'center',
                p: 3,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e0e0e0'
            }}>
                {enableCart && (
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleAddToCart}
                        startIcon={<CartIcon />}
                        sx={{ 
                            px: 4, 
                            py: 1.5,
                            fontSize: '16px',
                            fontWeight: 'bold'
                        }}
                    >
                        Ajouter au panier
                    </Button>
                )}
                
                {showWishlistButton && (
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleAddToWishlist}
                        startIcon={<WishlistIcon />}
                        sx={{ px: 3, py: 1.5 }}
                    >
                        Ajouter aux favoris
                    </Button>
                )}
                
                {showShareButton && (
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleShareProduct}
                        sx={{ px: 3, py: 1.5 }}
                    >
                        Partager le produit
                    </Button>
                )}
            </Box>

            {/* System Info */}
            <Box sx={{ 
                mt: 4, 
                p: 2, 
                backgroundColor: '#f0f0f0', 
                borderRadius: 1,
                fontSize: '12px',
                color: '#666'
            }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>System Information:</div>
                <div>Product ID: <span style={{ fontFamily: 'monospace' }}>{item._id}</span></div>
                {item.qwantaType && <div>Type: {item.qwantaType}</div>}
                {item.vi && <div>Template ID: <span style={{ fontFamily: 'monospace' }}>{item.vi}</span></div>}
                {item.di && <div>Domain: <span style={{ fontFamily: 'monospace' }}>{item.di}</span></div>}
                {item.ed && <div>Parent: <span style={{ fontFamily: 'monospace' }}>{item.ed}</span></div>}
            </Box>
        </Box>
    );
};

const ShopSettings = () => {
    const {
        actions: { setProp },
        name,
        productCatalogId,
        catalogDomain,
        cartCardId,
        width,
        height,
        viewMode,
        columnsPerRow,
        showShopHeader,
        showSearchInfo,
        showLoadingState,
        showFacets,
        showFacetCounts,
        facetConfig,
        enableSearch,
        showAdvancedSearch,
        enableCart,
        enableWishlist,
        showPricing,
        currency,
        showShoppingButtons,
        shoppingButtonsPosition,
        shoppingButtonsAlignment,
        useSmallButtons,
        showWishlistButton,
        showShareButton,
        cartButtonPosition,
        showCartButtonText,
        productTemplate,
        categoryTemplate,
        productDetailTemplate,
        useCompactRenderer,
        useFullTemplateMode,
        templateProjection,
        sideBySide,
        listWidth,
        showCapture,
        imageWidth,
        ratio,
        imageClassName,
        textClassName,
        animateClass,
        itemGap,
        showSeparator,
        separatorWidth,
        separatorColor,
        debug,
        showDeleteButton,
        searchFields,
        searchMode,
        itemHeight,
        // Cart configuration
        orderDomain,
        cartItemProperties,
        cartShowBorder,
        cartBorderColor,
        cartShowItemSeparators
    } = useNode((node) => ({
        name: node.data.props.name,
        productCatalogId: node.data.props.productCatalogId,
        catalogDomain: node.data.props.catalogDomain,
        cartCardId: node.data.props.cartCardId,
        width: node.data.props.width,
        height: node.data.props.height,
        viewMode: node.data.props.viewMode,
        columnsPerRow: node.data.props.columnsPerRow,
        showShopHeader: node.data.props.showShopHeader,
        showSearchInfo: node.data.props.showSearchInfo,
        showLoadingState: node.data.props.showLoadingState,
        showFacets: node.data.props.showFacets,
        showFacetCounts: node.data.props.showFacetCounts,
        facetConfig: node.data.props.facetConfig,
        enableSearch: node.data.props.enableSearch,
        showAdvancedSearch: node.data.props.showAdvancedSearch,
        enableCart: node.data.props.enableCart,
        enableWishlist: node.data.props.enableWishlist,
        showPricing: node.data.props.showPricing,
        currency: node.data.props.currency,
        showShoppingButtons: node.data.props.showShoppingButtons,
        shoppingButtonsPosition: node.data.props.shoppingButtonsPosition,
        shoppingButtonsAlignment: node.data.props.shoppingButtonsAlignment,
        useSmallButtons: node.data.props.useSmallButtons,
        showWishlistButton: node.data.props.showWishlistButton,
        showShareButton: node.data.props.showShareButton,
        cartButtonPosition: node.data.props.cartButtonPosition,
        showCartButtonText: node.data.props.showCartButtonText,
        productTemplate: node.data.props.productTemplate,
        categoryTemplate: node.data.props.categoryTemplate,
        productDetailTemplate: node.data.props.productDetailTemplate,
        useCompactRenderer: node.data.props.useCompactRenderer,
        useFullTemplateMode: node.data.props.useFullTemplateMode,
        templateProjection: node.data.props.templateProjection,
        sideBySide: node.data.props.sideBySide,
        listWidth: node.data.props.listWidth,
        showCapture: node.data.props.showCapture,
        imageWidth: node.data.props.imageWidth,
        ratio: node.data.props.ratio,
        imageClassName: node.data.props.imageClassName,
        textClassName: node.data.props.textClassName,
        animateClass: node.data.props.animateClass,
        itemGap: node.data.props.itemGap,
        showSeparator: node.data.props.showSeparator,
        separatorWidth: node.data.props.separatorWidth,
        separatorColor: node.data.props.separatorColor,
        debug: node.data.props.debug,
        showDeleteButton: node.data.props.showDeleteButton,
        searchFields: node.data.props.searchFields,
        searchMode: node.data.props.searchMode,
        itemHeight: node.data.props.itemHeight,
        // Cart configuration
        orderDomain: node.data.props.orderDomain,
        cartItemProperties: node.data.props.cartItemProperties,
        cartShowBorder: node.data.props.cartShowBorder,
        cartBorderColor: node.data.props.cartBorderColor,
        cartShowItemSeparators: node.data.props.cartShowItemSeparators
    }));

    const [localName, setLocalName] = useState(name);
    const [localProductCatalogId, setLocalProductCatalogId] = useState(productCatalogId);
    const [localCatalogDomain, setLocalCatalogDomain] = useState(catalogDomain);
    const [localCartCardId, setLocalCartCardId] = useState(cartCardId);
    const [localWidth, setLocalWidth] = useState(width);
    const [localHeight, setLocalHeight] = useState(height);
    const [localProductTemplate, setLocalProductTemplate] = useState(productTemplate);
    const [localCategoryTemplate, setLocalCategoryTemplate] = useState(categoryTemplate);
    const [localProductDetailTemplate, setLocalProductDetailTemplate] = useState(productDetailTemplate);
    const [localItemGap, setLocalItemGap] = useState(itemGap);
    const [localItemHeight, setLocalItemHeight] = useState(itemHeight);
    const [localSeparatorWidth, setLocalSeparatorWidth] = useState(separatorWidth);
    const [localSeparatorColor, setLocalSeparatorColor] = useState(separatorColor);

    return (
        <div>
            {/* Basic Settings */}
            <FormControl fullWidth margin="normal">
                <FormLabel>Shop Name</FormLabel>
                <TextField
                    value={localName || ''}
                    onChange={(e) => setLocalName(e.target.value)}
                    onBlur={(e) => setProp(props => props.name = e.target.value)}
                    size="small"
                    placeholder="Enter shop name"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Catalog Domain (Recommended)</FormLabel>
                <TextField
                    value={localCatalogDomain || ''}
                    onChange={(e) => setLocalCatalogDomain(e.target.value)}
                    onBlur={(e) => setProp(props => props.catalogDomain = e.target.value || null)}
                    size="small"
                    placeholder="e.g. autodin, electronics, fashion"
                    helperText="Domain name for your product catalog - gets all root-level products"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Product Catalog ID (Alternative)</FormLabel>
                <TextField
                    value={localProductCatalogId || ''}
                    onChange={(e) => setLocalProductCatalogId(e.target.value)}
                    onBlur={(e) => setProp(props => props.productCatalogId = e.target.value || null)}
                    size="small"
                    placeholder="Enter specific folder ID"
                    helperText="Specific folder ID - use if you need a particular folder within a domain"
                />
            </FormControl>

            {/* Search Features */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Search Features
                </FormLabel>
                
                <FormControlLabel
                    control={
                        <Switch
                            checked={enableSearch || false}
                            onChange={(e) => setProp(props => props.enableSearch = e.target.checked)}
                        />
                    }
                    label="Enable Product Search"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={showAdvancedSearch || false}
                            onChange={(e) => setProp(props => props.showAdvancedSearch = e.target.checked)}
                        />
                    }
                    label="Show Advanced Search Options"
                />
                
                {enableSearch && (
                    <>
                        <TextField
                            label="Search Fields"
                            value={searchFields ? searchFields.join(', ') : ''}
                            onChange={(e) => {
                                const fields = e.target.value.split(',').map(f => f.trim()).filter(f => f);
                                setProp(props => props.searchFields = fields);
                            }}
                            size="small"
                            fullWidth
                            margin="normal"
                            helperText="Comma-separated list of fields to search. Common fields: title, name, description, category, brand, tags, designationcourte, deslong, famille, marque, modele, code"
                            placeholder="title, name, description, category, brand, tags"
                        />
                        
                        <FormControl fullWidth margin="normal">
                            <FormLabel>Search Mode</FormLabel>
                            <Select
                                value={searchMode || 'partial'}
                                onChange={(e) => setProp(props => props.searchMode = e.target.value)}
                                size="small"
                            >
                                <MenuItem value="partial">Partial Match (finds substrings)</MenuItem>
                                <MenuItem value="exact">Exact Match (exact words only)</MenuItem>
                                <MenuItem value="fuzzy">Fuzzy Match (similar words)</MenuItem>
                            </Select>
                        </FormControl>
                    </>
                )}
            </Box>

            {/* Shopping Features */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Shopping Features
                </FormLabel>
                
                <FormControlLabel
                    control={
                        <Switch
                            checked={enableCart || false}
                            onChange={(e) => setProp(props => props.enableCart = e.target.checked)}
                        />
                    }
                    label="Enable Shopping Cart"
                />

                {enableCart && (
                    <FormControl fullWidth margin="normal">
                        <FormLabel>Cart Card ID (Optional)</FormLabel>
                        <TextField
                            value={localCartCardId || ''}
                            onChange={(e) => setLocalCartCardId(e.target.value)}
                            onBlur={(e) => setProp(props => props.cartCardId = e.target.value || null)}
                            size="small"
                            placeholder="Link to a Card component for cart display"
                            helperText="Connect to a Card component to display cart contents"
                        />
                    </FormControl>
                )}

                <FormControlLabel
                    control={
                        <Switch
                            checked={enableWishlist || false}
                            onChange={(e) => setProp(props => props.enableWishlist = e.target.checked)}
                        />
                    }
                    label="Enable Wishlist"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={showPricing || false}
                            onChange={(e) => setProp(props => props.showPricing = e.target.checked)}
                        />
                    }
                    label="Show Pricing"
                />

                {showPricing && (
                    <FormControl fullWidth margin="normal">
                        <FormLabel>Currency</FormLabel>
                        <Select
                            value={currency || "EUR"}
                            onChange={(e) => setProp(props => props.currency = e.target.value)}
                            size="small"
                        >
                            <MenuItem value="EUR">Euro (€)</MenuItem>
                            <MenuItem value="USD">US Dollar ($)</MenuItem>
                            <MenuItem value="GBP">British Pound (£)</MenuItem>
                            <MenuItem value="CHF">Swiss Franc (CHF)</MenuItem>
                        </Select>
                    </FormControl>
                )}

                {/* Shopping Buttons Configuration */}
                <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold', fontSize: '14px' }}>
                        Shopping Buttons Configuration
                    </FormLabel>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={showShoppingButtons || false}
                                onChange={(e) => setProp(props => props.showShoppingButtons = e.target.checked)}
                            />
                        }
                        label="Show Shopping Buttons in Detail View"
                    />

                    {showShoppingButtons && (
                        <>
                            <FormControl fullWidth margin="normal">
                                <FormLabel>Buttons Position</FormLabel>
                                <Select
                                    value={shoppingButtonsPosition || "bottom"}
                                    onChange={(e) => setProp(props => props.shoppingButtonsPosition = e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value="top">Top (above template)</MenuItem>
                                    <MenuItem value="bottom">Bottom (below template)</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl fullWidth margin="normal">
                                <FormLabel>Buttons Alignment</FormLabel>
                                <Select
                                    value={shoppingButtonsAlignment || "center"}
                                    onChange={(e) => setProp(props => props.shoppingButtonsAlignment = e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value="left">Left aligned</MenuItem>
                                    <MenuItem value="center">Center aligned</MenuItem>
                                    <MenuItem value="right">Right aligned</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={useSmallButtons || false}
                                        onChange={(e) => setProp(props => props.useSmallButtons = e.target.checked)}
                                    />
                                }
                                label="Use Small Buttons (compact size)"
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showWishlistButton || false}
                                        onChange={(e) => setProp(props => props.showWishlistButton = e.target.checked)}
                                    />
                                }
                                label="Show Wishlist Button"
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showShareButton || false}
                                        onChange={(e) => setProp(props => props.showShareButton = e.target.checked)}
                                    />
                                }
                                label="Show Share Product Button"
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showDeleteButton || false}
                                        onChange={(e) => setProp(props => props.showDeleteButton = e.target.checked)}
                                    />
                                }
                                label="Show Delete Button (Cross)"
                            />
                        </>
                    )}
                    
                    {enableCart && !sideBySide && (
                        <>
                            <FormControl fullWidth margin="normal">
                                <FormLabel>Cart Button Position</FormLabel>
                                <Select
                                    value={cartButtonPosition || "header"}
                                    onChange={(e) => setProp(props => props.cartButtonPosition = e.target.value)}
                                    size="small"
                                >
                                    <MenuItem value="header">Header (next to search)</MenuItem>
                                    <MenuItem value="searchbar">Search Bar (after sorting)</MenuItem>
                                </Select>
                            </FormControl>
                            
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showCartButtonText || false}
                                        onChange={(e) => setProp(props => props.showCartButtonText = e.target.checked)}
                                    />
                                }
                                label="Show Cart Button Text (show 'View Cart' text or just icon)"
                            />
                        </>
                    )}
                </Box>
            </Box>

            {/* Display Settings */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Display Settings
                </FormLabel>

                <FormControlLabel
                    control={
                        <Switch
                            checked={showShopHeader || false}
                            onChange={(e) => setProp(props => props.showShopHeader = e.target.checked)}
                        />
                    }
                    label="Show Shop Header (name, currency, cart info)"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={showSearchInfo || false}
                            onChange={(e) => setProp(props => props.showSearchInfo = e.target.checked)}
                        />
                    }
                    label="Show Search Info Bar (results count, sorting info)"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={showLoadingState || false}
                            onChange={(e) => setProp(props => props.showLoadingState = e.target.checked)}
                        />
                    }
                    label="Show Loading States (loading messages, completion status)"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={showFacets || false}
                            onChange={(e) => setProp(props => props.showFacets = e.target.checked)}
                        />
                    }
                    label="Show Filters/Facets (category, price range, etc.)"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={showFacetCounts || false}
                            onChange={(e) => setProp(props => props.showFacetCounts = e.target.checked)}
                        />
                    }
                    label="Show Facet Item Counts (number of items per filter option)"
                />

                {/* Facet Configuration */}
                {showFacets && (
                    <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold', fontSize: '14px' }}>
                            Configure Filters/Facets
                        </FormLabel>
                        
                        {(facetConfig || []).map((facet, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                <TextField
                                    size="small"
                                    label="Field"
                                    value={facet.field || ''}
                                    onChange={(e) => {
                                        const newFacetConfig = [...(facetConfig || [])];
                                        newFacetConfig[index] = { ...newFacetConfig[index], field: e.target.value };
                                        setProp(props => props.facetConfig = newFacetConfig);
                                    }}
                                    placeholder="e.g. category, brand, price"
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    size="small"
                                    label="Label"
                                    value={facet.label || ''}
                                    onChange={(e) => {
                                        const newFacetConfig = [...(facetConfig || [])];
                                        newFacetConfig[index] = { ...newFacetConfig[index], label: e.target.value };
                                        setProp(props => props.facetConfig = newFacetConfig);
                                    }}
                                    placeholder="e.g. Category, Brand, Price Range"
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                        const newFacetConfig = [...(facetConfig || [])];
                                        newFacetConfig.splice(index, 1);
                                        setProp(props => props.facetConfig = newFacetConfig);
                                    }}
                                    sx={{ minWidth: 'auto', px: 1 }}
                                >
                                    ×
                                </Button>
                            </Box>
                        ))}
                        
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                const newFacetConfig = [...(facetConfig || []), { field: '', label: '' }];
                                setProp(props => props.facetConfig = newFacetConfig);
                            }}
                            sx={{ mt: 1 }}
                        >
                            + Add Facet
                        </Button>
                    </Box>
                )}

                <FormControl fullWidth margin="normal">
                    <FormLabel>View Mode</FormLabel>
                    <Select
                        value={viewMode || "grid"}
                        onChange={(e) => setProp(props => props.viewMode = e.target.value)}
                        size="small"
                    >
                        <MenuItem value="flat">Flat List (Simple list view)</MenuItem>
                        <MenuItem value="grid">Grid View (Recommended for products)</MenuItem>
                        <MenuItem value="masonry">Masonry View (Pinterest-style)</MenuItem>
                        <MenuItem value="hybrid">Hybrid View (Categories + items)</MenuItem>
                        <MenuItem value="tree">Tree View (Hierarchical)</MenuItem>
                    </Select>
                </FormControl>

                {(viewMode === "grid" || viewMode === "masonry") && (
                    <FormControl fullWidth margin="normal">
                        <FormLabel>Products per Row</FormLabel>
                        <Select
                            value={columnsPerRow || 3}
                            onChange={(e) => setProp(props => props.columnsPerRow = e.target.value)}
                            size="small"
                        >
                            <MenuItem value={2}>2 columns</MenuItem>
                            <MenuItem value={3}>3 columns</MenuItem>
                            <MenuItem value={4}>4 columns</MenuItem>
                            <MenuItem value={5}>5 columns</MenuItem>
                        </Select>
                    </FormControl>
                )}

                <FormControl fullWidth margin="normal">
                    <FormLabel>Shop Width</FormLabel>
                    <TextField
                        value={localWidth || ''}
                        onChange={(e) => setLocalWidth(e.target.value)}
                        onBlur={(e) => setProp(props => props.width = e.target.value || '600px')}
                        size="small"
                        placeholder="e.g. 600px, 100%, 50vw"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Shop Height</FormLabel>
                    <TextField
                        value={localHeight || ''}
                        onChange={(e) => setLocalHeight(e.target.value)}
                        onBlur={(e) => setProp(props => props.height = e.target.value || '500px')}
                        size="small"
                        placeholder="e.g. 500px, 50vh, auto, 100%"
                        helperText="Use 100% for full height when inside TabControl or other flex containers"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Item Gap (Flat View)</FormLabel>
                    <TextField
                        type="number"
                        value={localItemGap !== undefined ? localItemGap : 0}
                        onChange={(e) => setLocalItemGap(parseInt(e.target.value) >= 0 ? parseInt(e.target.value) : 0)}
                        onBlur={(e) => setProp(props => props.itemGap = parseInt(e.target.value) >= 0 ? parseInt(e.target.value) : 0)}
                        size="small"
                        placeholder="Spacing between items in pixels"
                        helperText="Gap between items when using flat view display (pixels). Set to 0 for no gap."
                        inputProps={{ min: 0, max: 50 }}
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Item Height (Flat View)</FormLabel>
                    <TextField
                        type="number"
                        value={localItemHeight !== undefined ? localItemHeight : 120}
                        onChange={(e) => setLocalItemHeight(parseInt(e.target.value) >= 50 ? parseInt(e.target.value) : 50)}
                        onBlur={(e) => setProp(props => props.itemHeight = parseInt(e.target.value) >= 50 ? parseInt(e.target.value) : 50)}
                        size="small"
                        placeholder="Height of each item in pixels"
                        helperText="Fixed height for each item in the product list (pixels). Adjust this to match your template size."
                        inputProps={{ min: 50, max: 500 }}
                    />
                </FormControl>

                {/* Separator Settings */}
                <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold', fontSize: '14px' }}>
                        Separator (Flat View)
                    </FormLabel>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={showSeparator || false}
                                onChange={(e) => setProp(props => props.showSeparator = e.target.checked)}
                            />
                        }
                        label="Show separator line between items"
                    />

                    {showSeparator && (
                        <>
                            <FormControl fullWidth margin="normal">
                                <FormLabel>Separator Line Width</FormLabel>
                                <TextField
                                    type="number"
                                    value={localSeparatorWidth !== undefined ? localSeparatorWidth : 1}
                                    onChange={(e) => setLocalSeparatorWidth(parseInt(e.target.value) >= 1 ? parseInt(e.target.value) : 1)}
                                    onBlur={(e) => setProp(props => props.separatorWidth = parseInt(e.target.value) >= 1 ? parseInt(e.target.value) : 1)}
                                    size="small"
                                    placeholder="Line thickness in pixels"
                                    helperText="Thickness of the separator line (pixels). Minimum 1px."
                                    inputProps={{ min: 1, max: 10 }}
                                />
                            </FormControl>

                            <FormControl fullWidth margin="normal">
                                <FormLabel>Separator Color</FormLabel>
                                <TextField
                                    type="color"
                                    value={localSeparatorColor || "#e0e0e0"}
                                    onChange={(e) => setLocalSeparatorColor(e.target.value)}
                                    onBlur={(e) => setProp(props => props.separatorColor = e.target.value)}
                                    size="small"
                                    helperText="Color of the separator line between items"
                                />
                            </FormControl>
                        </>
                    )}
                </Box>
            </Box>

            {/* Template Settings */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Product Template
                </FormLabel>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Product Template ID</FormLabel>
                    <TextField
                        value={localProductTemplate || ''}
                        onChange={(e) => setLocalProductTemplate(e.target.value)}
                        onBlur={(e) => setProp(props => props.productTemplate = e.target.value || null)}
                        size="small"
                        placeholder="Enter template card ID for product display"
                        helperText="Use any Card component as template - Craft.js data will be used with ${item.fieldName} variable substitution"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Category Template ID (Optional)</FormLabel>
                    <TextField
                        value={localCategoryTemplate || ''}
                        onChange={(e) => setLocalCategoryTemplate(e.target.value)}
                        onBlur={(e) => setProp(props => props.categoryTemplate = e.target.value || null)}
                        size="small"
                        placeholder="Enter template card ID for category headers"
                        helperText="Use any Card component as template for category display with variable substitution"
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Product Detail Template ID (Optional)</FormLabel>
                    <TextField
                        value={localProductDetailTemplate || ''}
                        onChange={(e) => setLocalProductDetailTemplate(e.target.value)}
                        onBlur={(e) => setProp(props => props.productDetailTemplate = e.target.value || null)}
                        size="small"
                        placeholder="Enter template card ID for detail view"
                        helperText="Template for product detail view - if not set, shows all properties with shopping buttons"
                    />
                </FormControl>

                <FormControlLabel
                    control={
                        <Switch
                            checked={useFullTemplateMode || false}
                            onChange={(e) => setProp(props => props.useFullTemplateMode = e.target.checked)}
                        />
                    }
                    label="Full Template Mode (Template replaces entire item display)"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={useCompactRenderer || false}
                            onChange={(e) => setProp(props => props.useCompactRenderer = e.target.checked)}
                        />
                    }
                    label="Use Compact Renderer"
                />

                {/* Template Projection Configuration */}
                <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                    <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold', fontSize: '14px' }}>
                        Template Variable Fields
                    </FormLabel>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                        Configure which fields to fetch for template variable replacement (e.g., ${`{item.deslong}`} in templates)
                    </div>
                    
                    {(templateProjection || []).map((projection, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={projection.enabled || false}
                                        onChange={(e) => {
                                            const newProjection = [...(templateProjection || [])];
                                            newProjection[index] = { ...newProjection[index], enabled: e.target.checked };
                                            setProp(props => props.templateProjection = newProjection);
                                        }}
                                        size="small"
                                    />
                                }
                                label=""
                                sx={{ mr: 1 }}
                            />
                            <TextField
                                size="small"
                                label="Field Name"
                                value={projection.field || ''}
                                onChange={(e) => {
                                    const newProjection = [...(templateProjection || [])];
                                    newProjection[index] = { ...newProjection[index], field: e.target.value };
                                    setProp(props => props.templateProjection = newProjection);
                                }}
                                placeholder="e.g. deslong, price, category"
                                sx={{ flex: 1 }}
                                helperText={`Use as ${`{item.${projection.field || 'fieldname'}}`} in templates`}
                            />
                            <Button
                                size="small"
                                color="error"
                                onClick={() => {
                                    const newProjection = [...(templateProjection || [])];
                                    newProjection.splice(index, 1);
                                    setProp(props => props.templateProjection = newProjection);
                                }}
                                sx={{ minWidth: 'auto', px: 1 }}
                            >
                                ×
                            </Button>
                        </Box>
                    ))}
                    
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                            const newProjection = [...(templateProjection || []), { field: '', enabled: true }];
                            setProp(props => props.templateProjection = newProjection);
                        }}
                        sx={{ mt: 1 }}
                    >
                        + Add Field
                    </Button>
                </Box>
            </Box>

            {/* Advanced Layout */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Advanced Layout
                </FormLabel>

                <FormControlLabel
                    control={
                        <Switch
                            checked={sideBySide || false}
                            onChange={(e) => setProp(props => props.sideBySide = e.target.checked)}
                        />
                    }
                    label="Side-by-Side View (List + Detail)"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={showCapture || false}
                            onChange={(e) => setProp(props => props.showCapture = e.target.checked)}
                        />
                    }
                    label="Show Add Product Interface"
                />

                <FormControlLabel
                    control={
                        <Switch
                            checked={debug || false}
                            onChange={(e) => setProp(props => props.debug = e.target.checked)}
                        />
                    }
                    label="Debug Mode (show selection logs in console)"
                />
            </Box>

            {/* Cart Configuration */}
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Cart Configuration
                </FormLabel>
                
                <FormControl fullWidth margin="normal">
                    <FormLabel>Cart Item Properties</FormLabel>
                    <FormLabel component="legend" sx={{ mt: 1, mb: 1, fontSize: '12px', color: '#666' }}>
                        Configure which properties to display for cart items
                    </FormLabel>

                    {(cartItemProperties || []).map((prop, index) => (
                        <Box key={index} sx={{ border: '1px solid #eee', borderRadius: 1, p: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={prop.enabled}
                                            onChange={(e) => {
                                                const newProps = [...(cartItemProperties || [])];
                                                newProps[index] = { ...newProps[index], enabled: e.target.checked };
                                                setProp(props => props.cartItemProperties = newProps);
                                            }}
                                        />
                                    }
                                    label="Enable"
                                />
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                        const newProps = [...(cartItemProperties || [])];
                                        newProps.splice(index, 1);
                                        setProp(props => props.cartItemProperties = newProps);
                                    }}
                                    sx={{ minWidth: 'auto' }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    label="Field Name"
                                    size="small"
                                    value={prop.field}
                                    onChange={(e) => {
                                        const newProps = [...(cartItemProperties || [])];
                                        newProps[index] = { ...newProps[index], field: e.target.value };
                                        setProp(props => props.cartItemProperties = newProps);
                                    }}
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    label="Display Label"
                                    size="small"
                                    value={prop.label}
                                    onChange={(e) => {
                                        const newProps = [...(cartItemProperties || [])];
                                        newProps[index] = { ...newProps[index], label: e.target.value };
                                        setProp(props => props.cartItemProperties = newProps);
                                    }}
                                    sx={{ flex: 1 }}
                                />
                            </Box>
                        </Box>
                    ))}

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            const newProperty = {
                                field: 'newField',
                                label: 'New Property',
                                enabled: true
                            };
                            const newProps = [...(cartItemProperties || [])];
                            newProps.push(newProperty);
                            setProp(props => props.cartItemProperties = newProps);
                        }}
                        sx={{ mt: 1 }}
                        fullWidth
                    >
                        Add Cart Property
                    </Button>
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={cartShowBorder}
                                onChange={(e) => setProp(props => props.cartShowBorder = e.target.checked)}
                            />
                        }
                        label="Show Cart Border"
                    />
                </FormControl>

                {cartShowBorder && (
                    <FormControl fullWidth margin="normal">
                        <FormLabel>Cart Border Color</FormLabel>
                        <TextField
                            type="color"
                            value={cartBorderColor || '#e0e0e0'}
                            onChange={(e) => setProp(props => props.cartBorderColor = e.target.value)}
                            size="small"
                        />
                    </FormControl>
                )}

                <FormControl fullWidth margin="normal">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={cartShowItemSeparators}
                                onChange={(e) => setProp(props => props.cartShowItemSeparators = e.target.checked)}
                            />
                        }
                        label="Show Item Separators"
                    />
                </FormControl>
            </Box>
        </div>
    );
};

Shop.craft = {
    props: {
        name: "Shop",
        productCatalogId: null,
        catalogDomain: null,
        cartCardId: null,
        // Cart configuration
        orderDomain: "autodinStockOrder",
        cartItemProperties: [
            { field: 'designationcourte', label: 'Product Name', enabled: true },
            { field: 'code', label: 'Code', enabled: true },
            { field: 'deslong', label: 'Description', enabled: true },
            { field: 'famille', label: 'Family', enabled: true },
            { field: 'marque', label: 'Brand', enabled: false },
            { field: 'modele', label: 'Model', enabled: false },
            { field: 'brief', label: 'Brief', enabled: false },
            { field: 'category', label: 'Category', enabled: false }
        ],
        cartShowBorder: true,
        cartBorderColor: "#e0e0e0",
        cartShowItemSeparators: true,
        width: "600px",
        height: "500px",
        viewMode: "grid",
        columnsPerRow: 3,
        showShopHeader: false,
        showSearchInfo: false,
        showLoadingState: true,
        showFacets: false,
        showFacetCounts: false,
        facetConfig: [
            { field: 'category', label: 'Category' },
            { field: 'brand', label: 'Brand' },
            { field: 'price_range', label: 'Price Range' }
        ],
        enableSearch: true,
        showAdvancedSearch: false,
        enableCart: true,
        enableWishlist: false,
        showPricing: true,
        currency: "EUR",
        showShoppingButtons: true,
        shoppingButtonsPosition: "bottom",
        shoppingButtonsAlignment: "center",
        useSmallButtons: false,
        showWishlistButton: false,
        showShareButton: true,
        cartButtonPosition: "header",
        showCartButtonText: true,
        productTemplate: null,
        categoryTemplate: null,
        productDetailTemplate: null,
        useCompactRenderer: true,
        useFullTemplateMode: true,
        sideBySide: false,
        listWidth: "400px",
        showCapture: false,
        imageWidth: "120px",
        ratio: "1/1",
        imageClassName: "",
        textClassName: "",
        animateClass: "animate__fadeIn",
        debug: false,
        showDeleteButton: false,
        searchFields: ['title', 'name', 'description', 'category', 'brand', 'tags'],
        searchMode: 'partial',
        itemHeight: 120
    },
    rules: {
        canDrag: true
    },
    related: {
        settings: ShopSettings
    }
};